import { FlexCol, P, Span } from "@quarks";
import { RoomListWrapper } from "./styles";
import { Illustration, Loader } from "@atoms";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@hooks";
import { MeetingRoom, getMeetingRoomRequestState } from "@lib/store";
import { HaNRoomCell } from "@organisms";
import { FloorListHeader } from "../FloorListHeader";
import { useActiveMeetingRoomRequest, useHereAndNow } from "@pages";
import { useEffect, useRef } from "react";
import { FloorSuggestionsTile } from "../FloorSuggestions/FloorSuggestionsTile";
import { trackEvent } from "@lib/infrastructure";

export type HaNRoomListProps = {
  buildingId: string;
  onClick: (room: MeetingRoom) => void;
  startDateTime: string;
  endDateTime: string;
  selectedRoom: MeetingRoom | null;
};

/**
 * Lists available rooms in Here And Now
 */

export const HaNRoomList = ({ buildingId, onClick, startDateTime, endDateTime, selectedRoom }: HaNRoomListProps) => {
  const { t } = useTranslation();
  const { currentRoomDateTimes, tab, filterData, setHoverRoomId, selectedObject, selectedFloorId } = useHereAndNow();
  const request = useActiveMeetingRoomRequest();
  const meetingRoomLoadingStatus = useAppSelector(
    state => getMeetingRoomRequestState(state, request)?.selectedFloorLoadingStatus,
  );
  const rooms = filterData.rooms.filteredData;
  const loadingMeetingRooms = meetingRoomLoadingStatus === "loading" || meetingRoomLoadingStatus === undefined;
  const indexOfElement = selectedRoom ? rooms.indexOf(selectedRoom) : undefined;

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current
      ?.querySelector(`[data-han-list-item="${selectedObject?.id}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [indexOfElement, scrollRef, selectedObject]);

  const { above, below } = filterData.rooms.adjacentFilteredData;

  const aboveFloorId = above.at(0)?.floorId ?? null;
  const belowFloorId = below.at(0)?.floorId ?? null;

  return (
    <RoomListWrapper ref={scrollRef}>
      <FloorListHeader type="available" />

      {loadingMeetingRooms ? (
        <FlexCol
          flex="auto"
          alignItems="center"
          justifyContent="center">
          <Loader />
        </FlexCol>
      ) : rooms.length === 0 ? (
        <FlexCol
          alignItems="center"
          justifyContent="center"
          flex="auto"
          padding={32}
          gap={16}>
          <Illustration illustration="noResults" />
          <P
            color="currentColor"
            textAlign="center">
            {t("hereAndNow:NoRoomsAvailable")}
          </P>
        </FlexCol>
      ) : (
        <FlexCol
          rowGap={8}
          style={{ padding: "8px 12px" }}>
          {rooms.map((room, index) => (
            <Span
              key={room.id}
              data-han-list-item={room.id}
              onMouseEnter={() => setHoverRoomId(room.id)}
              onMouseLeave={() => setHoverRoomId(null)}>
              <HaNRoomCell
                room={room}
                index={index}
                onClick={onClick}
              />
            </Span>
          ))}
        </FlexCol>
      )}

      {(!!aboveFloorId || !!belowFloorId) && (
        <>
          <FloorListHeader type="suggestion" />
          <FlexCol
            rowGap={8}
            style={{ padding: "8px 12px" }}>
            {!!aboveFloorId && (
              <FloorSuggestionsTile
                floorId={aboveFloorId}
                direction={"up"}>
                {above.map((room, index) => (
                  <Span
                    key={room.id}
                    onMouseEnter={() => setHoverRoomId(room.id)}
                    onMouseLeave={() => setHoverRoomId(null)}>
                    <HaNRoomCell
                      room={room}
                      index={index}
                      onClick={e => {
                        onClick(e);
                        trackEvent("HereAndNow_OtherFloorSuggestions__SuggestionClick", {
                          buildingId,
                          tab,
                          floorId: aboveFloorId || "",
                          type: "above",
                          nodeId: room.id,
                        });
                      }}
                    />
                  </Span>
                ))}
              </FloorSuggestionsTile>
            )}

            {!!belowFloorId && (
              <FloorSuggestionsTile
                floorId={belowFloorId}
                direction={"down"}>
                {below.map((room, index) => (
                  <Span
                    key={room.id}
                    onMouseEnter={() => setHoverRoomId(room.id)}
                    onMouseLeave={() => setHoverRoomId(null)}>
                    <HaNRoomCell
                      room={room}
                      index={index}
                      onClick={e => {
                        onClick(e);
                        trackEvent("HereAndNow_OtherFloorSuggestions__SuggestionClick", {
                          buildingId,
                          tab,
                          floorId: belowFloorId || "",
                          type: "below",
                          nodeId: room.id,
                        });
                      }}
                    />
                  </Span>
                ))}
              </FloorSuggestionsTile>
            )}
          </FlexCol>
        </>
      )}
    </RoomListWrapper>
  );
};
